// cspell:disable
import { Provider } from "../order";
import { convertWeight, Weight, WeightUnit } from "../outbound-labels/util";

export enum FreightLocationType {
  AIRPORT = "AIRPORT",
  CARRIER_TERMINAL = "CARRIER_TERMINAL",
  COMMERCIAL = "COMMERCIAL",
  CONSTRUCTION = "CONSTRUCTION",
  DISTRIBUTION_CENTER = "DISTRIBUTION_CENTER",
  PIER_PORT_WARF = "PIER_PORT_WARF",
  GOVERNMENT_FACILITY = "GOVERNMENT_FACILITY",
  LIMITED_ACCESS = "LIMITED_ACCESS",
  RESIDENTIAL = "RESIDENTIAL",
  SECURED_ACCESS = "SECURED_ACCESS",
}

export const FreightLocationTypeLabels: Record<FreightLocationType, string> = {
  [FreightLocationType.AIRPORT]: "Airport",
  [FreightLocationType.CARRIER_TERMINAL]: "Carrier terminal",
  [FreightLocationType.COMMERCIAL]: "Commercial",
  [FreightLocationType.CONSTRUCTION]: "Construction",
  [FreightLocationType.DISTRIBUTION_CENTER]: "Distribution center",
  [FreightLocationType.PIER_PORT_WARF]: "Pier, port, or wharf",
  [FreightLocationType.GOVERNMENT_FACILITY]: "Government facility",
  [FreightLocationType.LIMITED_ACCESS]: "Limited access",
  [FreightLocationType.RESIDENTIAL]: "Residential",
  [FreightLocationType.SECURED_ACCESS]: "Secured access",
} as const;

export enum HandlingUnitType {
  BAG = "BAG",
  BALE = "BALE",
  BOX = "BOX",
  BUNDLE = "BUNDLE",
  CARTON = "CARTON",
  CASE = "CASE",
  CRATE = "CRATE",
  DRUM = "DRUM",
  PAIL = "PAIL",
  PALLET = "PLT",
  PIECES = "PIECES",
  REEL = "REEL",
  ROLL = "ROLL",
  SKID = "SKID",
  TANK = "TANK",
  TRAILER = "TRAILER",
}

export const HandlingUnitTypeLabels: Record<HandlingUnitType, string> = {
  [HandlingUnitType.BAG]: "Bag",
  [HandlingUnitType.BALE]: "Bale",
  [HandlingUnitType.BOX]: "Box",
  [HandlingUnitType.BUNDLE]: "Bundle",
  [HandlingUnitType.CARTON]: "Carton",
  [HandlingUnitType.CASE]: "Case",
  [HandlingUnitType.CRATE]: "Crate",
  [HandlingUnitType.DRUM]: "Drum",
  [HandlingUnitType.PAIL]: "Pail",
  [HandlingUnitType.PALLET]: "Pallet",
  [HandlingUnitType.PIECES]: "Pieces",
  [HandlingUnitType.REEL]: "Reel",
  [HandlingUnitType.ROLL]: "Roll",
  [HandlingUnitType.SKID]: "Skid",
  [HandlingUnitType.TANK]: "Tank",
  [HandlingUnitType.TRAILER]: "Trailer",
} as const;

export enum KnownSCAC {
  FEDEX_FREIGHT_ECONOMY = "FXNL",
  FEDEX_FREIGHT_PRIORITY = "FXFE",
  ABF_FREIGHT = "ABFS",
  CROSS_COUNTRY_FREIGHT = "CCYQ",
  XPO_LOGISTICS = "CNWY",
  TFORCE_FREIGHT = "UPGF",
  US_ROAD_FREIGHT_EXPRESS = "USRD",
  FORWARD_AIR = "FWRA",
  OLD_DOMINION_FREIGHT_LINE = "ODFL",
  RL_CARRIERS = "RLCA",
  AAA_COOPER = "AACT",
  ROADRUNNER_FREIGHT = "RDFS",
  ESTES = "EXLA",
  SAIA = "SAIA",
  WARP_TECHNOLOGY = "WTCH",
  CENTRAL_TRANSPORT = "CTII",
  OAK_HARBOR_FREIGHT = "OAKH",
  VOCAR_TRANSPORTATION = "VCTS",
  SOUTHWESTERN_MOTOR_TRANSPORT = "SMTL",
  DAYLIGHT_TRANSPORT = "DYLT",
  SOUTHEASTERN_FREIGHT_LINES = "SEFL",
  DUGAN_TRUCK_LINE = "DUBL",
  AVERITT = "AVRT",
  PITT_OHIO = "PITD",
  MORAN = "MTJG",
  N_M_TRANSFER = "NMTF",
  DOHRN = "DHRN",
  DAYTON_FREIGHT = "DAFG",
}

export const knownScacToLabel: Record<KnownSCAC, string> = {
  [KnownSCAC.FEDEX_FREIGHT_ECONOMY]: "FedEx Freight Economy",
  [KnownSCAC.FEDEX_FREIGHT_PRIORITY]: "FedEx Freight Priority",
  [KnownSCAC.ABF_FREIGHT]: "ABF Freight",
  [KnownSCAC.CROSS_COUNTRY_FREIGHT]: "Cross Country Freight Solutions",
  [KnownSCAC.XPO_LOGISTICS]: "XPO Logistics",
  [KnownSCAC.TFORCE_FREIGHT]: "TForce Freight",
  [KnownSCAC.US_ROAD_FREIGHT_EXPRESS]: "U.S. Road Freight Express",
  [KnownSCAC.FORWARD_AIR]: "Forward Air",
  [KnownSCAC.OLD_DOMINION_FREIGHT_LINE]: "Old Dominion Freight Line",
  [KnownSCAC.RL_CARRIERS]: "R + L Carriers",
  [KnownSCAC.AAA_COOPER]: "AAA Cooper Transportation",
  [KnownSCAC.ROADRUNNER_FREIGHT]: "Roadrunner Freight",
  [KnownSCAC.ESTES]: "Estes",
  [KnownSCAC.SAIA]: "SAIA",
  [KnownSCAC.WARP_TECHNOLOGY]: "Warp Technology",
  [KnownSCAC.CENTRAL_TRANSPORT]: "Central Transport",
  [KnownSCAC.OAK_HARBOR_FREIGHT]: "Oak Harbor Freight",
  [KnownSCAC.VOCAR_TRANSPORTATION]: "Vocar Transportation",
  [KnownSCAC.SOUTHWESTERN_MOTOR_TRANSPORT]: "Southwestern Motor Transport",
  [KnownSCAC.DAYLIGHT_TRANSPORT]: "Daylight Transport",
  [KnownSCAC.SOUTHEASTERN_FREIGHT_LINES]: "Southeastern Freight Lines",
  [KnownSCAC.DUGAN_TRUCK_LINE]: "Dugan Truck Line",
  [KnownSCAC.AVERITT]: "Averitt",
  [KnownSCAC.PITT_OHIO]: "Pitt Ohio",
  [KnownSCAC.MORAN]: "Moran Transportation",
  [KnownSCAC.N_M_TRANSFER]: "N & M Transfer",
  [KnownSCAC.DOHRN]: "Dohrn",
  [KnownSCAC.DAYTON_FREIGHT]: "Dayton Freight",
};

// Each marketplace's fulfill method needs to be updated to check for freight carrier
// and pass that to the marketplace. Some marketplaces may not support freight carriers
// as well, so for now only Shopify and Manual are supported. Add support as needed.
export const supportedFreightMarketplaces = [
  Provider.SHOPIFY,
  Provider.MANUAL,
  Provider.NETSUITE,
];

export enum FreightServiceLevel {
  STANDARD = "STANDARD",
  GUARANTEED = "GUARANTEED",
}

export enum FreightChargeCategory {
  FREIGHT = "FREIGHT",
  FUEL = "FUEL",
  ACCESSORIAL = "ACCESSORIAL",
}

export enum FreightClass {
  CLASS_50 = "50",
  CLASS_55 = "55",
  CLASS_60 = "60",
  CLASS_65 = "65",
  CLASS_70 = "70",
  CLASS_77_5 = "77.5",
  CLASS_85 = "85",
  CLASS_92_5 = "92.5",
  CLASS_100 = "100",
  CLASS_110 = "110",
  CLASS_125 = "125",
  CLASS_150 = "150",
  CLASS_175 = "175",
  CLASS_200 = "200",
  CLASS_250 = "250",
  CLASS_300 = "300",
  CLASS_400 = "400",
  CLASS_500 = "500",
}

// Other things can affect freight class, but density is the most common way to calculate it.
// Ranges provided by WWEX
export function calculateFreightClass({
  weight,
  dimensionsInInches,
  numberOfHandlingUnits,
}: {
  weight: Weight;
  dimensionsInInches: { length: number; width: number; height: number };
  numberOfHandlingUnits: number;
}): FreightClass {
  const weightInPounds = convertWeight(weight, WeightUnit.POUND).value;
  const volumeInCubicFeet =
    (dimensionsInInches.length *
      dimensionsInInches.width *
      dimensionsInInches.height) /
    1728;

  if (
    volumeInCubicFeet <= 0 ||
    numberOfHandlingUnits <= 0 ||
    weightInPounds <= 0
  ) {
    return FreightClass.CLASS_50;
  }

  // Calculates density per handling unit
  const density = weightInPounds / volumeInCubicFeet / numberOfHandlingUnits;

  if (density < 1) {
    return FreightClass.CLASS_400;
  }
  if (density >= 1 && density < 2) {
    return FreightClass.CLASS_300;
  }
  if (density >= 2 && density < 4) {
    return FreightClass.CLASS_250;
  }
  if (density >= 4 && density < 6) {
    return FreightClass.CLASS_175;
  }
  if (density >= 6 && density < 8) {
    return FreightClass.CLASS_125;
  }
  if (density >= 8 && density < 10) {
    return FreightClass.CLASS_100;
  }
  if (density >= 10 && density < 12) {
    return FreightClass.CLASS_92_5;
  }
  if (density >= 12 && density < 15) {
    return FreightClass.CLASS_85;
  }
  if (density >= 15 && density < 22.5) {
    return FreightClass.CLASS_70;
  }
  if (density >= 22.5 && density < 30) {
    return FreightClass.CLASS_65;
  }
  if (density >= 30 && density < 35) {
    return FreightClass.CLASS_60;
  }
  if (density >= 35 && density < 50) {
    return FreightClass.CLASS_55;
  }
  if (density >= 50) {
    return FreightClass.CLASS_50;
  }
  throw new Error(`Invalid density: ${density}`);
}

export enum FreightProvider {
  WWEX = "WWEX",
}
